import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useCurrentValueGetter } from 'hooks';
import { ENTITY } from 'modules/campaigns/config';
import { AllEntityId } from 'modules/campaigns/config/allEntityId';
import { useReportEntityIds } from 'modules/campaigns/hooks/useReportEntityIds';
import { useReportEntityType } from 'modules/campaigns/hooks/useReportEntityType';
import { useChatPromptVersion } from '../../../../components/ChatAgent/hooks/useChatPromptVersion';
import { accountsMapSelector } from '../../../../selectors/baseSelectors';
import { userSelector } from '../../authentication';
import { getUrl, getContext } from '../utils/chatAgentContextService';
import { useContextAccountsData } from './useContextAccountsData';

export const useChatAgentContextService = () => {
    const { user_id } = useSelector(userSelector);
    const accountMap = useSelector(accountsMapSelector);
    const accountMapGetter = useCurrentValueGetter(accountMap);
    const location = useLocation();
    const locationGetter = useCurrentValueGetter(location);
    const urlGetter = useCallback(context => getUrl(context, accountMapGetter()), [accountMapGetter]);
    const userIdGetter = useCurrentValueGetter(user_id);
    const getAccountsData = useContextAccountsData();
    const [reportEntityIds] = useReportEntityIds();
    const [reportEntityType] = useReportEntityType();
    const campaignId = reportEntityType === ENTITY.CAMPAIGN ? reportEntityIds : AllEntityId;
    const campaignsIdGetter = useCurrentValueGetter(campaignId);
    const [chatPromptVersion] = useChatPromptVersion();
    const chatPromptVersionGetter = useCurrentValueGetter(chatPromptVersion);

    const contextGetter = useCallback(async () => {
        const { accountName, globalAccountName } = await getAccountsData();
        const { pathname, search } = locationGetter();
        const resolvedUrl = `${pathname}${search}`;

        return getContext({
            url: resolvedUrl,
            userId: userIdGetter(),
            accountMap: accountMapGetter(),
            accountName,
            globalAccountName,
            campaignId: campaignsIdGetter(),
            promptVersion: chatPromptVersionGetter(),
        });
    }, [locationGetter, userIdGetter, accountMapGetter, getAccountsData, campaignsIdGetter, chatPromptVersionGetter]);

    return { getContext: contextGetter, getUrl: urlGetter };
};
